import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { requestAnnotationSheetAccess } from "../services/AnnotationSheetAccessService";
import { createAnnotationSheetAccessNotice } from "../utils/annotationSheetAccessNotice";

const HEARTBEAT_INTERVAL_MS = 10_000;
const REACQUIRE_AFTER_HEARTBEAT_REASONS = new Set([
  "session-not-active",
  "editor-active",
  "editor-active-own-tab",
]);

const createSessionId = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `annotation-session-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const ViewModeContext = createContext({
  sessionId: null,
  mode: null,
  isViewMode: true,
  isTrackEditMode: false,
  accessReady: false,
  accessHealthy: false,
  isTransitioning: false,
  notice: null,
  acquireAccess: async () => false,
  switchToViewing: async () => false,
  switchToEditing: async () => false,
  switchToTrackEditing: async () => false,
  clearNotice: () => {},
});

export const ViewModeProvider = ({ children }) => {
  const sessionIdRef = useRef(createSessionId());
  const activeSheetIdRef = useRef(null);
  const modeRef = useRef(null);
  const accessReadyRef = useRef(false);
  const acquisitionVersionRef = useRef(0);
  const heartbeatInFlightRef = useRef(false);
  const mountedRef = useRef(true);

  const [mode, setMode] = useState(null);
  const [isTrackEditMode, setIsTrackEditMode] = useState(false);
  const [accessReady, setAccessReady] = useState(false);
  const [accessHealthy, setAccessHealthy] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [notice, setNotice] = useState(null);

  const updateMode = useCallback((nextMode) => {
    modeRef.current = nextMode;
    setMode(nextMode);
  }, []);

  const markReady = useCallback((ready) => {
    accessReadyRef.current = ready;
    setAccessReady(ready);
  }, []);

  const clearNotice = useCallback(() => setNotice(null), []);

  const showRequestFailure = useCallback((error, action) => {
    if (!mountedRef.current) return;
    const nextNotice = createAnnotationSheetAccessNotice({ error, action });
    setNotice(nextNotice);
    if (nextNotice.exitOnDismiss) {
      setAccessHealthy(false);
      setIsTrackEditMode(false);
    }
  }, []);

  const performTransition = useCallback(async (
    action,
    sheetId = activeSheetIdRef.current,
    acquisitionVersion = null
  ) => {
    if (!sheetId) return false;

    setIsTransitioning(true);
    try {
      const response = await requestAnnotationSheetAccess({
        sheetId,
        sessionId: sessionIdRef.current,
        action,
      });
      if (!mountedRef.current) return false;
      if (
        acquisitionVersion !== null &&
        acquisitionVersion !== acquisitionVersionRef.current
      ) {
        if (activeSheetIdRef.current !== sheetId) {
          Promise.resolve(requestAnnotationSheetAccess({
            sheetId,
            sessionId: sessionIdRef.current,
            action: "release",
            keepalive: true,
          })).catch(() => {});
        }
        return false;
      }
      updateMode(response.mode);
      markReady(true);
      setAccessHealthy(true);
      setNotice(null);
      return true;
    } catch (error) {
      showRequestFailure(error, action);
      return false;
    } finally {
      if (mountedRef.current) setIsTransitioning(false);
    }
  }, [markReady, showRequestFailure, updateMode]);

  const acquireAccess = useCallback(async (sheetId, requestedMode) => {
    const acquisitionVersion = acquisitionVersionRef.current + 1;
    acquisitionVersionRef.current = acquisitionVersion;
    const previousSheetId = activeSheetIdRef.current;
    if (
      previousSheetId &&
      previousSheetId !== sheetId &&
      accessReadyRef.current
    ) {
      try {
        await requestAnnotationSheetAccess({
          sheetId: previousSheetId,
          sessionId: sessionIdRef.current,
          action: "release",
        });
      } catch (error) {
        showRequestFailure(error, "release");
        setAccessHealthy(false);
        return false;
      }
      if (acquisitionVersion !== acquisitionVersionRef.current) return false;
    }

    activeSheetIdRef.current = sheetId;
    updateMode(null);
    markReady(false);
    setAccessHealthy(false);
    setIsTrackEditMode(false);
    return performTransition(`acquire-${requestedMode}`, sheetId, acquisitionVersion);
  }, [markReady, performTransition, showRequestFailure, updateMode]);

  const switchToEditing = useCallback(async () => {
    if (modeRef.current === "edit" && accessHealthy) {
      setIsTrackEditMode(false);
      return true;
    }
    if (modeRef.current !== "view" || !accessHealthy) return false;

    const changed = await performTransition("upgrade");
    if (changed) setIsTrackEditMode(false);
    return changed;
  }, [accessHealthy, performTransition]);

  const switchToTrackEditing = useCallback(async () => {
    if (modeRef.current === "edit" && accessHealthy) {
      setIsTrackEditMode(true);
      return true;
    }
    if (modeRef.current !== "view" || !accessHealthy) return false;

    const changed = await performTransition("upgrade");
    if (changed) setIsTrackEditMode(true);
    return changed;
  }, [accessHealthy, performTransition]);

  const switchToViewing = useCallback(async () => {
    if (modeRef.current === "view" && accessHealthy) {
      setIsTrackEditMode(false);
      return true;
    }
    if (modeRef.current !== "edit" || !accessHealthy) return false;

    const changed = await performTransition("downgrade");
    if (changed) setIsTrackEditMode(false);
    return changed;
  }, [accessHealthy, performTransition]);

  useEffect(() => {
    if (!accessReady || !activeSheetIdRef.current) return undefined;

    const intervalId = setInterval(async () => {
      if (heartbeatInFlightRef.current) return;
      heartbeatInFlightRef.current = true;
      const sheetId = activeSheetIdRef.current;
      const acquisitionVersion = acquisitionVersionRef.current;
      try {
        await requestAnnotationSheetAccess({
          sheetId,
          sessionId: sessionIdRef.current,
          action: "heartbeat",
        });
        if (mountedRef.current) setAccessHealthy(true);
      } catch (error) {
        if (!mountedRef.current) return;
        setAccessHealthy(false);
        const reason = error?.payload?.reason;
        if (
          error?.kind === "conflict" &&
          REACQUIRE_AFTER_HEARTBEAT_REASONS.has(reason)
        ) {
          const recoveryAction = modeRef.current === "edit"
            ? "acquire-edit"
            : "acquire-view";
          await performTransition(recoveryAction, sheetId, acquisitionVersion);
        } else {
          setIsTrackEditMode(false);
          showRequestFailure(error, "heartbeat");
        }
      } finally {
        heartbeatInFlightRef.current = false;
      }
    }, HEARTBEAT_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [accessReady, performTransition, showRequestFailure]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      if (!accessReadyRef.current || !activeSheetIdRef.current) return;
      Promise.resolve(requestAnnotationSheetAccess({
        sheetId: activeSheetIdRef.current,
        sessionId: sessionIdRef.current,
        action: "release",
        keepalive: true,
      })).catch(() => {});
    };
  }, []);

  const value = useMemo(() => ({
    sessionId: sessionIdRef.current,
    mode,
    isViewMode: mode !== "edit" || !accessHealthy,
    isTrackEditMode,
    accessReady,
    accessHealthy,
    isTransitioning,
    notice,
    acquireAccess,
    switchToViewing,
    switchToEditing,
    switchToTrackEditing,
    clearNotice,
  }), [
    accessHealthy,
    accessReady,
    acquireAccess,
    clearNotice,
    isTrackEditMode,
    isTransitioning,
    notice,
    mode,
    switchToEditing,
    switchToTrackEditing,
    switchToViewing,
  ]);

  return (
    <ViewModeContext.Provider value={value}>
      {children}
    </ViewModeContext.Provider>
  );
};

export const useViewMode = () => useContext(ViewModeContext);
